import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelL } from '@sorare/core/src/atoms/typography';
import { LoanWrapper } from '@sorare/core/src/components/card/Loan';
import { Dialog } from '@sorare/core/src/components/dialog';
import { FlexCard } from '@sorare/marketplace/src/components/token/FlexCard';

import type { ExtraCardDialog_anyCard } from './__generated__/index.graphql.ts';
import pack from './assets/pack.webp';

const StyledDialog = styled(Dialog)`
  background: linear-gradient(180deg, #2c2b70 0%, rgba(44, 43, 112, 0) 100%),
    var(--c-black);
`;

const Content = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--quadruple-unit);
  overflow: hidden;
`;

const RevealWrapper = styled.div`
  position: relative;
  aspect-ratio: var(--card-aspect-ratio);
  width: 260px;
  margin: var(--double-unit) 0;
`;

const PackImage = styled.img`
  position: absolute;

  &.animated {
    animation: openAnimation 1s ease-in-out forwards;
    @keyframes openAnimation {
      0% {
        transform: scale(1) translateY(0%);
        opacity: 1;
      }
      100% {
        transform: scale(1.1) translateY(110%);
        opacity: 0;
      }
    }
  }
`;

const CardContainer = styled.div`
  text-align: center;
  position: absolute;
  top: 0;

  opacity: 0;

  &.visible {
    opacity: 1;
  }

  &.revealed {
    animation: revealedAnimation 0.3s ease-in-out forwards 0.5s;
    @keyframes revealedAnimation {
      0% {
        transform: scale(1) translateY(100%);
        opacity: 0;
      }
      20% {
        transform: scale(1.02) translateY(100%);
        opacity: 1;
      }
      100% {
        transform: scale(1.02);
        opacity: 1;
      }
    }
  }
`;

export const ExtraCardDialog = ({
  card,
  onDone,
}: {
  card: ExtraCardDialog_anyCard;
  onDone?: () => void;
}) => {
  const [revealed, setRevealed] = useState(false);

  return (
    <StyledDialog
      maxWidth="xs"
      open
      hideHeader
      {...(revealed ? { onDone } : {})}
    >
      <Content center>
        <LabelL color="var(--c-nd-600)" className="text-center" bold>
          <FormattedMessage
            id="LoanTeamSelection.extraCardReveal.description"
            defaultMessage="As a reward here's another card!"
          />
        </LabelL>
        <BrandM className="text-center">
          <FormattedMessage
            id="LoanTeamSelection.extraCardReveal.yourBonusLoanCardV2"
            defaultMessage="Bonus loan card"
          />
        </BrandM>
        <RevealWrapper>
          <PackImage
            className={classNames({ animated: revealed })}
            width="260px"
            height="420px"
            alt=""
            src={pack}
          />
          <CardContainer className={classNames({ revealed })}>
            <LoanWrapper card={card}>
              <FlexCard card={card} width={320} withLink={false} />
            </LoanWrapper>
          </CardContainer>
        </RevealWrapper>
        {revealed ? (
          <Button fullWidth size="large" color="primary" onClick={onDone}>
            <FormattedMessage
              id="LoanTeamSelection.extraCardReveal.cta"
              defaultMessage="Set my lineup"
            />
          </Button>
        ) : (
          <Button
            fullWidth
            size="large"
            color="primary"
            onClick={() => setRevealed(true)}
          >
            <FormattedMessage
              id="LoanTeamSelection.extraCardReveal.revealed.cta"
              defaultMessage="Claim"
            />
          </Button>
        )}
      </Content>
    </StyledDialog>
  );
};

ExtraCardDialog.fragments = {
  anyCard: gql`
    fragment ExtraCardDialog_anyCard on AnyCardInterface {
      slug
      ...FlexCard_anyCard
      ...LoanWrapper_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${LoanWrapper.fragments.anyCard}
  ` as TypedDocumentNode<ExtraCardDialog_anyCard>,
};
